import { styleConstants } from '@paywithextend/components'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { BudgetScreen } from 'src/app/CreateBudget/CreateBudget'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import ChooseObjectTypeBox from 'src/components/ChooseObjectTypeBox/ChooseObjectTypeBox'
import { ImageNames } from 'src/enums'
import { useFlyoutDispatch } from 'src/hooks'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import { useIsUserOrglessRCNUser } from 'src/hooks/useOrglessUserStatus'
import { useTranslate } from 'src/localization/Trns'
import { createBudgetUrl, requestBudgetUrl } from 'src/navigation/urls'
import { createSetIsSetupOrganizationPopupOpen } from 'src/redux/actions/app.actions'
import { selectIsConfirmBudgetModalCreateBudgetDismissed } from 'src/redux/selectors/auth.selectors'
import styled from 'styled-components'

interface BudgetPickContentProps {
  setScreen: (val: BudgetScreen) => void
  standardDisabled?: boolean
  recurringDisabled?: boolean
  requesting?: boolean
}

const ChooseCreateObjectTypeBox = styled(ChooseObjectTypeBox)`
  &:nth-child(n + 2) {
    margin-top: ${styleConstants.heights.minorInternalGutter};
  }
`

const BudgetPickContent: React.FC<BudgetPickContentProps> = (props) => {
  const translate = useTranslate()

  const { analytics } = useAnalyticsContext()
  // Fetches CreditCardsSourceCount itself (replaces the legacy mount
  // dispatch); true only once the count has loaded successfully, so it also
  // subsumes the legacy statisticsLoaded gate.
  const isUserOrglessRCNUser = useIsUserOrglessRCNUser()
  const isConfirmBudgetModalDismissed = useSelector(
    selectIsConfirmBudgetModalCreateBudgetDismissed,
  )
  const dispatch = useFlyoutDispatch()
  const showRequestBudget = props.requesting
  const budgetStandardActiveImage = useColorOrGrayscaleImage(
    ImageNames.BudgetStandardActive,
  )
  const budgetRecurringActiveImage = useColorOrGrayscaleImage(
    ImageNames.BudgetRecurringActive,
  )

  useEffect(() => {
    if (isUserOrglessRCNUser && !isConfirmBudgetModalDismissed) {
      dispatch(createSetIsSetupOrganizationPopupOpen(true))
    }
  }, [isUserOrglessRCNUser])

  return (
    <ActionRightFlyoutContent
      title={translate(
        showRequestBudget ? 'Request a Budget' : 'Create a Budget',
      )}
      pickerSubtitle={translate(
        showRequestBudget
          ? 'Choose the type of budget you need to request.'
          : 'Choose the type of budget you need to create.',
      )}
      permissions={{
        isBudget: true,
      }}
    >
      <ChooseCreateObjectTypeBox
        imageSrc={budgetStandardActiveImage}
        title={translate('Standard Budget')}
        subtitle={translate(
          'Give your team the power to issue virtual cards without exceeding allocated funds.',
        )}
        onClick={(): void => {
          props.setScreen(BudgetScreen.Standard)
          analytics?.track(
            showRequestBudget
              ? 'request budget flyout - request standard budget - button press'
              : 'create budget flyout - create standard budget - button press',
            {
              interaction: 'button press',
              intent: showRequestBudget
                ? 'request standard budget'
                : 'create standard budget',
              screen: showRequestBudget
                ? requestBudgetUrl()
                : createBudgetUrl(),
            },
          )
        }}
        disabled={props.standardDisabled}
        id={'standardBudget'}
      />
      <ChooseCreateObjectTypeBox
        imageSrc={budgetRecurringActiveImage}
        id={'recurringBudget'}
        title={translate('Recurring Budget')}
        subtitle={translate(
          'Recurring budgets are great for giving teams monthly allowances or managing recurring expenses.',
        )}
        onClick={(): void => {
          props.setScreen(BudgetScreen.Recurring)
          analytics?.track(
            showRequestBudget
              ? 'request budget flyout - request recurring budget - button press'
              : 'create budget flyout - create recurring budget - button press',
            {
              interaction: 'button press',
              intent: showRequestBudget
                ? 'request recurring budget'
                : 'create standard budget',
              screen: showRequestBudget
                ? requestBudgetUrl()
                : createBudgetUrl(),
            },
          )
        }}
        disabled={props.recurringDisabled}
      />
    </ActionRightFlyoutContent>
  )
}

export default BudgetPickContent
